import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  LinearProgress,
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSnackbar } from 'notistack';

interface UsageMonitorProps {
  variant?: 'compact' | 'full';
  showUpgradeButton?: boolean;
}

const UsageMonitor: React.FC<UsageMonitorProps> = ({ 
  variant = 'compact', 
  showUpgradeButton = true 
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { subscription, getRemainingReels } = useSubscription();
  const [alertShown, setAlertShown] = useState<string | null>(null);

  const usagePercentage = subscription?.plan.reelsLimit === -1 
    ? 0 
    : (subscription?.reelsUsedThisMonth || 0) / (subscription?.plan.reelsLimit || 1) * 100;

  const remaining = getRemainingReels();
  const isUnlimited = subscription?.plan.reelsLimit === -1;

  // Alert thresholds
  const ALERT_80_PERCENT = 80;
  const ALERT_90_PERCENT = 90;
  const ALERT_100_PERCENT = 100;

  useEffect(() => {
    // Show usage alerts
    if (!isUnlimited && !alertShown) {
      if (usagePercentage >= ALERT_100_PERCENT) {
        enqueueSnackbar('You have reached your monthly reel limit!', { variant: 'error' });
        setAlertShown('100');
      } else if (usagePercentage >= ALERT_90_PERCENT) {
        enqueueSnackbar(`Warning: Only ${remaining} reels remaining this month!`, { variant: 'warning' });
        setAlertShown('90');
      } else if (usagePercentage >= ALERT_80_PERCENT) {
        enqueueSnackbar(`You've used 80% of your monthly reels`, { variant: 'info' });
        setAlertShown('80');
      }
    }
  }, [usagePercentage, remaining, isUnlimited, alertShown, enqueueSnackbar]);

  // Determine alert severity
  const getAlertSeverity = () => {
    if (usagePercentage >= ALERT_100_PERCENT) return 'error';
    if (usagePercentage >= ALERT_90_PERCENT) return 'warning';
    if (usagePercentage >= ALERT_80_PERCENT) return 'info';
    return 'success';
  };

  const getProgressColor = () => {
    if (usagePercentage >= ALERT_100_PERCENT) return '#ef4444';
    if (usagePercentage >= ALERT_90_PERCENT) return '#f59e0b';
    if (usagePercentage >= ALERT_80_PERCENT) return '#3b82f6';
    return '#10b981';
  };

  // Compact variant for dashboard
  if (variant === 'compact') {
    return (
      <Box sx={{ mb: 2 }}>
        {!isUnlimited && usagePercentage >= ALERT_80_PERCENT && (
          <Alert 
            severity={getAlertSeverity()}
            action={
              showUpgradeButton && usagePercentage >= ALERT_90_PERCENT ? (
                <Button 
                  color="inherit" 
                  size="small"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade
                </Button>
              ) : null
            }
          >
            {usagePercentage >= ALERT_100_PERCENT ? (
              <>
                <strong>Limit Reached!</strong> Upgrade to continue creating reels.
              </>
            ) : (
              <>
                <strong>{remaining} reels remaining</strong> - {Math.round(usagePercentage)}% of monthly limit used
              </>
            )}
          </Alert>
        )}
      </Box>
    );
  }

  // Full variant for detailed view
  return (
    <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Usage Monitor
          </Typography>
          {subscription?.hasByoaAddon && (
            <Chip 
              label="BYOA Active" 
              color="secondary" 
              size="small"
              icon={<TrendingUpIcon />}
            />
          )}
        </Box>
        {showUpgradeButton && !isUnlimited && (
          <Button
            variant={usagePercentage >= ALERT_90_PERCENT ? 'contained' : 'outlined'}
            size="small"
            onClick={() => navigate('/pricing')}
            color={usagePercentage >= ALERT_90_PERCENT ? 'primary' : 'inherit'}
          >
            {usagePercentage >= ALERT_100_PERCENT ? 'Upgrade Now' : 'View Plans'}
          </Button>
        )}
      </Box>

      {/* Usage Stats */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Monthly Usage
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {isUnlimited 
              ? `${subscription?.reelsUsedThisMonth || 0} reels (Unlimited)`
              : `${subscription?.reelsUsedThisMonth || 0} / ${subscription?.plan.reelsLimit || 0} reels`
            }
          </Typography>
        </Box>
        
        {!isUnlimited && (
          <>
            <LinearProgress
              variant="determinate"
              value={Math.min(usagePercentage, 100)}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: getProgressColor(),
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {Math.round(usagePercentage)}% used
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {remaining} remaining
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Cost Monitoring (BYOA vs Managed) */}
      {subscription?.hasByoaAddon && (
        <Alert severity="success" icon={<TrendingUpIcon />}>
          <AlertTitle>You're saving ${subscription?.plan.byoaDiscount || 0}/month with BYOA!</AlertTitle>
          <Typography variant="body2">
            Managing your own API keys gives you full control over costs and usage.
          </Typography>
        </Alert>
      )}

      {/* Hard Limits Warning */}
      {usagePercentage >= ALERT_100_PERCENT && !isUnlimited && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 2 }}>
          <AlertTitle>Hard Limit Reached</AlertTitle>
          <Typography variant="body2">
            You cannot create more reels this month. Upgrade your plan or wait for the next billing cycle.
          </Typography>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ mt: 1 }}
            onClick={() => navigate('/pricing')}
          >
            Upgrade Plan
          </Button>
        </Alert>
      )}

      {/* Usage Tips */}
      {usagePercentage >= ALERT_80_PERCENT && usagePercentage < ALERT_100_PERCENT && !isUnlimited && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
          <AlertTitle>Usage Tip</AlertTitle>
          <Typography variant="body2">
            Consider scheduling your remaining {remaining} reels strategically or upgrade to {
              subscription?.plan.type === 'basic' ? 'Pro' : 'Enterprise'
            } for more monthly reels.
          </Typography>
        </Alert>
      )}

      {/* API Cost Estimate */}
      {!subscription?.hasByoaAddon && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Estimated API costs covered by your plan: ~${((subscription?.reelsUsedThisMonth || 0) * 0.53).toFixed(2)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UsageMonitor;